import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { driveIngest, type FolderEntry, type IngestSourceRow } from '@/lib/driveIngest';
import {
  FILE_TYPES, folderPath, isGoogleAuthUrl, normalizeFileTypes, statusLabel, statusTone, syncErrorMessage, type Provider,
} from '@/lib/driveIngestLogic';
import { Folder, ChevronRight, Loader2, RefreshCw, Trash2, Plus } from 'lucide-react';
import { useLocalization } from '@/hooks/useLocalization';

const LABEL: Record<Provider, string> = { google: 'Google Drive', microsoft: 'Microsoft 365 (OneDrive / SharePoint)' };

interface Crumb { id: string; name: string; driveId?: string }
interface Picked { id: string; driveId?: string; path: string }

function FolderBrowser({ provider, onPick }: { provider: Provider; onPick: (f: Picked) => void }) {
  const { t } = useLocalization();
  // Breadcrumb stack; empty = the provider's roots (Microsoft roots are drives and carry a driveId).
  const [stack, setStack] = useState<Crumb[]>([]);
  const top = stack[stack.length - 1];
  const { data, isLoading, error } = useQuery({
    queryKey: ['drive-browse', provider, top?.id ?? 'roots', top?.driveId],
    queryFn: () => (top ? driveIngest.children(provider, top.id, top.driveId) : driveIngest.roots(provider)),
  });
  const path = folderPath(stack);

  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground truncate" aria-live="polite">{path || t('Choose a location')}</div>
      <ul className="border rounded-md max-h-64 overflow-y-auto divide-y" aria-label={t('Folders')} aria-busy={isLoading}>
        {isLoading && <li className="p-3 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />  {t('Loading…')}</li>}
        {error && <li className="p-3 text-sm text-destructive" role="alert">{(error as Error).message}</li>}
        {data?.entries.length === 0 && <li className="p-3 text-sm text-muted-foreground">{t('No folders here.')}</li>}
        {data?.entries.map((f: FolderEntry) => (
          <li key={`${f.driveId ?? ''}:${f.id}`}>
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('Open folder {0}', { 0: f.name })}
              onClick={() => setStack([...stack, { id: f.id, name: f.name, driveId: f.driveId ?? top?.driveId }])}
            >
              <span className="flex items-center gap-2"><Folder className="h-4 w-4" aria-hidden="true" />{f.name}</span>
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" disabled={stack.length === 0} onClick={() => setStack(stack.slice(0, -1))}>{t('Up')}</Button>
        <Button type="button" size="sm" disabled={!top} onClick={() => top && onPick({ id: top.id, driveId: top.driveId, path })}>{t('Select this folder')}</Button>
      </div>
    </div>
  );
}

export function DriveIngestPanel() {
  const { tx, t } = useLocalization();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [adding, setAdding] = useState<Provider | null>(null);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [includeSub, setIncludeSub] = useState(true);
  const [types, setTypes] = useState<string[]>([...FILE_TYPES]);
  const [removing, setRemoving] = useState<IngestSourceRow | null>(null);
  const [disconnecting, setDisconnecting] = useState<Provider | null>(null);

  const status = useQuery({ queryKey: ['drive-status'], queryFn: driveIngest.status, refetchInterval: 15_000 });
  const refresh = () => qc.invalidateQueries({ queryKey: ['drive-status'] });
  const onError = (e: unknown) => toast({ title: t('Something went wrong'), description: e instanceof Error ? e.message : t('Please try again.'), variant: 'destructive' });
  const resetAdd = () => { setPicked(null); setIncludeSub(true); setTypes([...FILE_TYPES]); };
  const openAdd = (p: Provider) => { resetAdd(); setAdding(p); };
  const closeAdd = () => { setAdding(null); resetAdd(); };

  const connectGoogle = useMutation({
    mutationFn: driveIngest.googleAuthUrl,
    onSuccess: ({ url }) => {
      if (!isGoogleAuthUrl(url)) { onError(new Error('Unexpected response from the server.')); return; }
      window.location.href = url;
    },
    onError,
  });

  const addSource = useMutation({
    mutationFn: async () => {
      if (!adding || !picked) throw new Error('Choose a folder first.');
      const provider = adding;
      const created = await driveIngest.addSource({
        provider, externalFolderId: picked.id, driveId: picked.driveId, displayPath: picked.path,
        includeSubfolders: includeSub, fileTypes: normalizeFileTypes(types),
      });
      // A failed/busy sync trigger must not make the (already saved) folder look un-added.
      const synced = await driveIngest.syncNow(provider).then(() => true, () => false);
      return { created, synced };
    },
    onSuccess: ({ synced }) => {
      closeAdd();
      toast({ title: t('Folder added'), description: synced ? t('The first sync has started.') : t('It will sync on the next scheduled run, or use Sync now.') });
      refresh();
    },
    onError,
  });

  const removeSource = useMutation({
    mutationFn: (id: string) => driveIngest.removeSource(id),
    onSuccess: () => { setRemoving(null); toast({ title: t('Folder removed'), description: t('Its files and search data were deleted.') }); refresh(); },
    onError: (e) => { setRemoving(null); onError(e); },
  });
  const syncNow = useMutation({
    mutationFn: (p?: Provider) => driveIngest.syncNow(p),
    onSuccess: () => { toast({ title: t('Sync started') }); refresh(); },
    onError: (e) => toast({ title: t('Could not start sync'), description: syncErrorMessage(e), variant: 'destructive' }),
  });
  const disconnect = useMutation({
    mutationFn: (p: Provider) => driveIngest.disconnect(p),
    onSuccess: () => { setDisconnecting(null); toast({ title: t('Disconnected'), description: t('Ingested files from this provider were deleted.') }); refresh(); },
    onError: (e) => { setDisconnecting(null); onError(e); },
  });

  const providers: Provider[] = ['google', 'microsoft'];
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('Project documents (Google Drive & Microsoft 365)')}</CardTitle>
          <CardDescription>{t('Pick the folders whose documents Masira may read. Access is read-only and limited to the folders you select here.')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {status.isLoading && <div className="text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />  {t('Loading…')}</div>}
          {status.error && <div className="text-sm text-destructive" role="alert">{(status.error as Error).message}</div>}
          {status.data && providers.map((p) => {
            const connected = status.data[p].connected;
            const sources = status.data.sources.filter((s) => s.provider === p);
            return (
              <section key={p} className="space-y-3" aria-label={LABEL[p]}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="font-medium">{LABEL[p]}</h3>
                  <div className="flex gap-2">
                    {!connected && p === 'google' && (
                      <Button size="sm" onClick={() => connectGoogle.mutate()} disabled={connectGoogle.isPending}>
                        {connectGoogle.isPending ? t('Redirecting…') : t('Connect Google Drive')}
                      </Button>
                    )}
                    {!connected && p === 'microsoft' && <span className="text-sm text-muted-foreground">{t('Connect Microsoft 365 above first.')}</span>}
                    {connected && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => openAdd(p)}><Plus className="h-4 w-4 mr-1" aria-hidden="true" />{t('Add folder')}</Button>
                        <Button size="sm" variant="outline" onClick={() => syncNow.mutate(p)} disabled={syncNow.isPending && syncNow.variables === p}>
                          <RefreshCw className={`h-4 w-4 mr-1 ${syncNow.isPending && syncNow.variables === p ? 'animate-spin' : ''}`} aria-hidden="true" />
                          {syncNow.isPending && syncNow.variables === p ? t('Syncing…') : t('Sync now')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDisconnecting(p)}>{p === 'google' ? t('Disconnect') : t('Remove all folders')}</Button>
                      </>
                    )}
                  </div>
                </div>
                {connected && sources.length === 0 && <p className="text-sm text-muted-foreground">{t('No folders selected yet.')}</p>}
                {sources.map((s) => {
                  const label = statusLabel(s.lastStatus);
                  return (
                    <div key={s.id} className="flex items-center justify-between gap-3 border rounded-md px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm truncate" title={s.displayPath}>{s.displayPath}</div>
                        <div className="text-xs text-muted-foreground">
                          {s.includeSubfolders ? t('Including subfolders') : t('This folder only')}
                          {s.fileTypes?.length ? ` · ${s.fileTypes.join(', ')}` : ''}
                          {s.lastSyncedAt ? t(' · last synced {0}', { 0: new Date(s.lastSyncedAt).toLocaleString() }) : t(' · not synced yet')}
                        </div>
                        {s.lastStatus === 'failed' && s.lastError && <div className="text-xs text-destructive break-words">{s.lastError}</div>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {label && <Badge variant={statusTone(s.lastStatus)}>{tx(label)}</Badge>}
                        {s.lastStatus === 'needs_reauth' && p === 'google' && (
                          <Button size="sm" variant="outline" onClick={() => connectGoogle.mutate()} disabled={connectGoogle.isPending}>{t('Reconnect')}</Button>
                        )}
                        <Button size="icon" variant="ghost" aria-label={t('Remove folder {0}', { 0: s.displayPath })} title={t('Remove folder')} onClick={() => setRemoving(s)}>
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {sources.some((s) => s.lastStatus === 'needs_reauth') && p === 'microsoft' && (
                  <p className="text-xs text-muted-foreground">{t('Microsoft access needs to be re-approved: use Reconnect in the Microsoft 365 card above.')}</p>
                )}
              </section>
            );
          })}
        </CardContent>
      </Card>

      <Dialog open={!!adding} onOpenChange={(o) => !o && !addSource.isPending && closeAdd()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Add a')} {adding ? LABEL[adding] : ''}  {t('folder')}</DialogTitle>
            <DialogDescription>{t('Browse to the project folder and select it. Only the file types ticked below are ingested.')}</DialogDescription>
          </DialogHeader>
          {adding && <FolderBrowser provider={adding} onPick={setPicked} />}
          {picked && (
            <div className="space-y-3">
              <div className="text-sm">{t('Selected:')} <span className="font-medium">{picked.path}</span></div>
              <div className="flex items-center gap-2">
                <Checkbox id="incl-sub" checked={includeSub} onCheckedChange={(v) => setIncludeSub(v === true)} />
                <Label htmlFor="incl-sub">{t('Include subfolders')}</Label>
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">{t('File types')}</legend>
                <div className="flex flex-wrap gap-4">
                  {FILE_TYPES.map((t) => (
                    <div key={t} className="flex items-center gap-2">
                      <Checkbox
                        id={`ft-${t}`}
                        checked={types.includes(t)}
                        onCheckedChange={(v) => setTypes((cur) => (v === true ? [...cur, t] : cur.filter((x) => x !== t)))}
                      />
                      <Label htmlFor={`ft-${t}`}>{t.toUpperCase()}</Label>
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeAdd} disabled={addSource.isPending}>{t('Cancel')}</Button>
            <Button disabled={!picked || types.length === 0 || addSource.isPending} onClick={() => addSource.mutate()}>
              {addSource.isPending ? t('Adding…') : t('Add folder & sync')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Remove this folder?')}</AlertDialogTitle>
            <AlertDialogDescription>
              
              {t('Masira will stop reading “')}{removing?.displayPath}{t('” and permanently delete the files and search chunks it ingested from it. Your Drive files are not changed.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={removeSource.isPending}
              onClick={(e) => { e.preventDefault(); if (removing) removeSource.mutate(removing.id); }}
            >
              {removeSource.isPending ? t('Removing…') : t('Remove and delete data')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!disconnecting} onOpenChange={(o) => !o && setDisconnecting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{disconnecting === 'google' ? t('Disconnect Google Drive?') : t('Remove all Microsoft folders?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {disconnecting === 'google'
                ? t('Masira will revoke its access, stop syncing, and permanently delete all files and search chunks ingested from Google Drive. Your Drive files are not changed.')
                : t('Masira will stop syncing and permanently delete all files and search chunks ingested from OneDrive / SharePoint folders. Microsoft 365 admin consent is not revoked.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={disconnect.isPending}
              onClick={(e) => { e.preventDefault(); if (disconnecting) disconnect.mutate(disconnecting); }}
            >
              {disconnect.isPending ? t('Working…') : t('Disconnect and delete data')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
